import {
  Create,
  Datagrid,
  Edit,
  List,
  SaveButton,
  SimpleForm,
  TextField,
  Toolbar,
  EditButton,
  DateField,
  BooleanField,
  NullableBooleanInput,
  AutocompleteInput,
  ReferenceInput,
  FunctionField,
  TextInput,
  SelectInput,
  PasswordInput,
  FormDataConsumer,
  useRecordContext,
  useNotify,
} from "react-admin";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";

const token = JSON.parse(localStorage.getItem("token"));

const authHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: "Bearer " + token,
});

// Lets an admin assign/unassign states to a manager account (Admins Management is admin-only already)
const ManagerStatesAssignment = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const currentUserRole = localStorage.getItem("permissions");
  const [allStates, setAllStates] = useState([]);
  const [assignedStateIds, setAssignedStateIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingStateId, setSavingStateId] = useState(null);

  useEffect(() => {
    if (!record?.id || currentUserRole !== "admin") {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statesRes, assignedRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_URL}/admin/state`, {
            headers: { Accept: "application/json" },
          }),
          fetch(
            `${process.env.REACT_APP_URL}/admin-state-assignments/admin/${record.id}`,
            { headers: authHeaders() }
          ),
        ]);
        const states = await statesRes.json();
        const assigned = await assignedRes.json();
        setAllStates(states);
        setAssignedStateIds((assigned || []).map((s) => s.id));
      } catch (err) {
        notify("Error loading state assignments", { type: "warning" });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  if (!record || currentUserRole !== "admin") return null;

  const toggleState = async (stateId, checked) => {
    setSavingStateId(stateId);
    try {
      if (checked) {
        const res = await fetch(
          `${process.env.REACT_APP_URL}/admin-state-assignments`,
          {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ adminId: record.id, stateId }),
          }
        );
        if (!res.ok) throw new Error("Failed to assign state");
        setAssignedStateIds((prev) => [...prev, stateId]);
      } else {
        const res = await fetch(
          `${process.env.REACT_APP_URL}/admin-state-assignments/${record.id}/${stateId}`,
          { method: "DELETE", headers: authHeaders() }
        );
        if (!res.ok) throw new Error("Failed to remove state");
        setAssignedStateIds((prev) => prev.filter((id) => id !== stateId));
      }
    } catch (err) {
      notify(err.message, { type: "error" });
    } finally {
      setSavingStateId(null);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Assigned States
      </Typography>
      {loading ? (
        <CircularProgress size={20} />
      ) : (
        <FormGroup>
          {allStates.map((state) => (
            <FormControlLabel
              key={state.id}
              control={
                <Checkbox
                  checked={assignedStateIds.includes(state.id)}
                  disabled={savingStateId === state.id}
                  onChange={(e) => toggleState(state.id, e.target.checked)}
                />
              }
              label={state.name}
            />
          ))}
        </FormGroup>
      )}
    </Box>
  );
};

// Displays the assigned state(s) for a manager row in the Admins list
const ManagerStatesField = () => {
  const record = useRecordContext();
  const [stateNames, setStateNames] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!record?.id || record.role !== "manager") return;
    setLoading(true);
    fetch(
      `${process.env.REACT_APP_URL}/admin-state-assignments/admin/${record.id}`,
      { headers: authHeaders() }
    )
      .then((res) => res.json())
      .then((data) => setStateNames((data || []).map((s) => s.name)))
      .catch(() => setStateNames([]))
      .finally(() => setLoading(false));
  }, [record?.id, record?.role]);

  if (record?.role !== "manager") return null;
  if (loading) return <CircularProgress size={14} />;
  if (stateNames.length === 0) return <span>-</span>;
  return <span>{stateNames.join(", ")}</span>;
};

export const AdminsList = (props) => {
  return (
    <List {...props}>
      <Datagrid rowClick="edit">
        <TextField source="name" />
        <TextField source="username" />
        <TextField source="phonenumber" />
        <TextField source="role" />
        <ManagerStatesField label="States" />
        <FunctionField
          label="Created At"
          render={(record) =>
            `${new Date(record.created_at).toLocaleString()} `
          }
        />

        <EditButton></EditButton>
      </Datagrid>
    </List>
  );
};

export const AdminsCreate = (props) => {
  return (
    <Create title="Create a Sub-Category " {...props} redirect="list">
      <SimpleForm>
        <TextInput source="name" />
        <TextInput source="username" />
        <TextInput source="phonenumber" />
        <SelectInput
          source="role"
          choices={[
            { id: "admin", name: "Admin" },
            { id: "manager", name: "Manager" },
            { id: "operater", name: "Operater" },
            { id: "knowledgeManger", name: "KnowledgeManger" },
            { id: "supervisor", name: "Supervisor" },
          ]}
        />
        <PasswordInput source="password" initiallyVisible />
      </SimpleForm>
    </Create>
  );
};

const UserEditToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton alwaysEnable />
  </Toolbar>
);
const transformAdminEdit = (data) => {
  if (!data.password) {
    const { password, ...rest } = data;
    return rest;
  }
  return data;
};

export const AdminsEdit = (props) => {
  return (
    <Edit {...props} transform={transformAdminEdit}>
      <SimpleForm toolbar={<UserEditToolbar />}>
        <TextInput source="name" />
        <TextInput source="username" />
        <TextInput source="phonenumber" />
        <SelectInput
          source="role"
          choices={[
            { id: "admin", name: "Admin" },
            { id: "manager", name: "Manager" },
            { id: "operater", name: "Operater" },
            { id: "knowledgeManger", name: "KnowledgeManger" },
            { id: "supervisor", name: "Supervisor" },
          ]}
        />
        <PasswordInput source="password" initiallyVisible helperText="Leave blank to keep the current password" />
        <FormDataConsumer>
          {({ formData }) =>
            formData.role === "manager" && <ManagerStatesAssignment />
          }
        </FormDataConsumer>
      </SimpleForm>
    </Edit>
  );
};
